import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, LOCALE_ID, Optional } from '@angular/core';
import { SelectOption, SelectOptions } from '@cbuild/controls';
import {
  CsHttpClientService,
  StorageService
} from '@cbuild/cs-common-data-services';
import { toQueryString } from '@cbuild/utils';
import { map, Observable, ReplaySubject, Subject, take } from 'rxjs';
import { ProfileInfo } from './models/profile-info.model';
import { ProjectSwitching } from './project.switching';
import { PropertyFilter } from './property-filter';

function hexToRGB(hex: string): { r: number; g: number; b: number } {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

export type PropertyOption = SelectOption & { propertyTypeId: number, serviceKey: string };

export type PropertiesList = Array<PropertyOption>;

// export type PropertiesList = Array<Property>;

// export interface Property {
//   label: string;
//   value: number;
//   propertyTypeId: PropertyType;
// }
/**
 * @dynamic
 */

@Injectable({
  providedIn: 'root'
})
export class ApplicationService extends ProjectSwitching {
  private _userInfo: ReplaySubject<ProfileInfo> = new ReplaySubject(1);
  public basicInfo: ProfileInfo | undefined;
  private _notifyCallPopupClose = new Subject<number>();

  constructor(
    private storageService: StorageService,
    private http: CsHttpClientService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) @Optional() public locale: string
  ) {
    super();
    // this.updates.versionUpdates.subscribe(event => {
    //   if (event.type === 'VERSION_READY') {
    //     this.updates.activateUpdate().then(() => document.location.reload());
    //   }
    // });
  }

  public loadBasicInfo(): void {
    this.http
      .read<Array<ProfileInfo>>('api/account/basic-info/get')
      .pipe(
        map(res => res[0]),
        take(1)
      )
      .subscribe(resp => {
        this.basicInfo = resp;
        this.setUserInfo(resp);
      });
  }

  public get $userInfo(): Observable<ProfileInfo> {
    return this._userInfo.asObservable();
  }

  /**
   * sets user profile info
   */
  public setUserInfo(userInfo: ProfileInfo): void {
    if (userInfo) {
      this.basicInfo = { ...this.basicInfo, ...userInfo };
      this._userInfo.next(this.basicInfo);
      return;
    }
    throw new Error('userInfo is required');
  }

  public updateTheme(primary: string, secondary: string): void {
    this.document.documentElement.style.setProperty(
      '--cs-primary-color',
      primary
    );
    this.document.documentElement.style.setProperty(
      '--cs-secondary-color',
      secondary
    );
    {
      const { r, g, b } = hexToRGB(primary);
      this.document.documentElement.style.setProperty(
        '--cs-primary-rgb',
        `${r}, ${g}, ${b}`
      );
    }
    {
      const { r, g, b } = hexToRGB(secondary);
      this.document.documentElement.style.setProperty(
        '--cs-secondary-rgb',
        `${r}, ${g}, ${b}`
      );
    }
  }

  public updatePropertyFilterObservable(propertyFilter: PropertyFilter): void {
    this.propertyFilter = propertyFilter;
    this._propertyFilter.next(propertyFilter);
  }

  public setPropertyFilter(propertyFilter?: PropertyFilter): void {
    const toSet: PropertyFilter = propertyFilter || {
      companyId: null,
      propertyId: null,
      propertyTypeId: null
    };
    this.updatePropertyFilterObservable(toSet);
    this.storageService.setItem('gp_filter', toSet);
  }

  public resetState(): void {
    super.resetState();
    this._userInfo = new ReplaySubject(1);
    this.basicInfo = undefined;
  }

  public getProjects(companyId: number | null): Observable<PropertiesList> {
    return this.http.read<PropertiesList>(
      `api/common/properties/get${toQueryString({ companyId })}`
    );
  }

  public getCompanies(): Observable<SelectOptions> {
    return this.http.read<SelectOptions>('api/common/companies');
  }

  public get $notifyCallPopupClose(): Observable<number> {
    return this._notifyCallPopupClose.asObservable();
  }

  public setNotifyCallPopupClose(leadId: number): void {
    if (leadId) {
      this._notifyCallPopupClose.next(leadId);
      return;
    }
  }
}
